import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { cn } from '../../../utils/cn';

const CompanyProfileStep = ({ data, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    companyName: data?.companyName || '',
    legalName: data?.legalName || '',
    yearEstablished: data?.yearEstablished || '',
    employeeCount: data?.employeeCount || '',
    annualRevenue: data?.annualRevenue || '',
    headquarters: data?.headquarters || '',
    description: data?.description || '',
    ...data
  });

  const [errors, setErrors] = useState({});

  const employeeRanges = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501+', label: '501+ employees' }
  ];

  const revenueRanges = [
    { value: 'under_1m', label: 'Under $1M' },
    { value: '1m_5m', label: '$1M - $5M' },
    { value: '5m_10m', label: '$5M - $10M' },
    { value: '10m_50m', label: '$10M - $50M' },
    { value: '50m+', label: '$50M+' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.companyName?.trim()) newErrors.companyName = 'Company name is required';
    if (!formData?.legalName?.trim()) newErrors.legalName = 'Legal name is required';
    if (!formData?.yearEstablished) newErrors.yearEstablished = 'Year established is required';
    if (!formData?.employeeCount) newErrors.employeeCount = 'Employee count is required';
    if (!formData?.headquarters?.trim()) newErrors.headquarters = 'Headquarters location is required';
    if (!formData?.description?.trim()) newErrors.description = 'Company description is required';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      onNext?.(formData);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-foreground">Company Profile</h2>
        <p className="text-muted-foreground">
          Provide detailed information about your organization.
        </p>
      </div>
      {/* Form */}
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
          
          <Input
            label="Company Name"
            required
            value={formData?.companyName}
            onChange={(e) => handleChange('companyName', e?.target?.value)}
            error={errors?.companyName}
            placeholder="Acme Travel Services"
          />

          <Input
            label="Legal Business Name"
            required
            value={formData?.legalName}
            onChange={(e) => handleChange('legalName', e?.target?.value)}
            error={errors?.legalName}
            placeholder="Acme Travel Services LLC"
            description="As registered with government authorities"
          />

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Year Established"
              type="number"
              required
              value={formData?.yearEstablished}
              onChange={(e) => handleChange('yearEstablished', e?.target?.value)}
              error={errors?.yearEstablished}
              placeholder="2010"
              min="1900"
              max={new Date()?.getFullYear()}
            />

            <Input
              label="Headquarters Location"
              required
              value={formData?.headquarters}
              onChange={(e) => handleChange('headquarters', e?.target?.value)}
              error={errors?.headquarters}
              placeholder="New York, NY, USA"
            />
          </div>
        </div>

        {/* Company Size */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-foreground">Company Size</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="Number of Employees"
              required
              options={employeeRanges}
              value={formData?.employeeCount}
              onChange={(value) => handleChange('employeeCount', value)}
              error={errors?.employeeCount}
              placeholder="Select range"
            />

            <Select
              label="Annual Revenue"
              options={revenueRanges}
              value={formData?.annualRevenue}
              onChange={(value) => handleChange('annualRevenue', value)}
              placeholder="Select range (optional)"
            />
          </div>
        </div>

        {/* Company Description */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Company Description <span className="text-destructive">*</span>
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleChange('description', e?.target?.value)}
            placeholder="Describe your company's mission, services, and unique value proposition..."
            rows={6}
            className={cn(
              'w-full rounded-md border backdrop-blur-sm bg-white/10 border-white/20 text-foreground px-3 py-2 text-sm',
              'placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
              errors?.description && 'border-destructive focus:ring-destructive'
            )}
          />
          {errors?.description && (
            <p className="text-sm text-destructive">{errors?.description}</p>
          )}
        </div>
      </div>
      {/* Actions */}
      <div className="flex justify-between pt-6 border-t border-white/20">
        <Button
          variant="outline"
          iconName="ArrowLeft"
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          iconName="ArrowRight"
          iconPosition="right"
          onClick={handleNext}
        >
          Continue to Service Offerings
        </Button>
      </div>
    </div>
  );
};

export default CompanyProfileStep;